import React, { useEffect, useState, useMemo } from "react";
import { App, Select, Spin, Transfer, Radio, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdOutlineLocationOn } from "react-icons/md";
import Card from "../../components/controls/Card";
import AppControl from "../../components/";
import {
  getPincodes,
  getUniqueStates,
  getDistrictsByState,
  assignLocalityType,
} from "../../store/slice/pincodes";

const LOCALITY_TYPES = ["Metro", "Urban", "Suburban", "Rural"];

function PincodeAssign() {
  const [targetKeys, setTargetKeys] = useState([]);
  const [localityType, setLocalityType] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { list, uniqueStates, districtsByState, isLoading, isAssigning } =
    useSelector((state) => state.pincodes);

  useEffect(() => {
    dispatch(getPincodes());
    dispatch(getUniqueStates());
  }, [dispatch]);

  const stateOptions = useMemo(
    () => (uniqueStates ?? []).map((s) => ({ value: s, label: s })),
    [uniqueStates],
  );

  const districtOptions = useMemo(
    () => (districtsByState ?? []).map((d) => ({ value: d, label: d })),
    [districtsByState],
  );

  // Deduplicate list by pincode, keeping state/district for filtering
  const allPincodeItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    for (const p of list ?? []) {
      if (!seen.has(p.pincode)) {
        seen.add(p.pincode);
        items.push({
          key: p.pincode,
          title: p.pincode,
          state: p.state,
          district: p.district,
        });
      }
    }
    return items;
  }, [list]);

  // Filter left panel by state/district; always keep selected items in dataSource
  const dataSource = useMemo(() => {
    if (!selectedState) return allPincodeItems;

    const filtered = allPincodeItems.filter(
      (p) =>
        p.state === selectedState &&
        (!selectedDistrict || p.district === selectedDistrict),
    );

    // Items already in the right panel must stay visible even if they don't match the filter
    const filteredKeys = new Set(filtered.map((p) => p.key));
    const extra = allPincodeItems.filter(
      (p) => targetKeys.includes(p.key) && !filteredKeys.has(p.key),
    );

    return [...filtered, ...extra];
  }, [allPincodeItems, selectedState, selectedDistrict, targetKeys]);

  const handleStateChange = (val) => {
    setSelectedState(val ?? null);
    setSelectedDistrict(null);
    if (val) dispatch(getDistrictsByState(val));
  };

  const handleAssign = async () => {
    if (!targetKeys.length) {
      message.warning("Please select at least one pincode.");
      return;
    }
    if (!localityType) {
      message.warning("Please select a locality type.");
      return;
    }
    const result = await dispatch(
      assignLocalityType({ pincodes: targetKeys, localityType }),
    );
    if (assignLocalityType.fulfilled.match(result)) {
      message.success(result.payload.message);
      setTargetKeys([]);
      setLocalityType(null);
    } else {
      message.error(result.error?.message ?? "Assignment failed.");
    }
  };

  const canAssign = targetKeys.length > 0 && localityType;

  return (
    <Card title="ASSIGN PINCODE LOCALITY TYPE">
      {/* State / District filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <Select
            placeholder="All states"
            style={{ width: "100%" }}
            options={stateOptions}
            value={selectedState}
            onChange={handleStateChange}
            loading={isLoading}
            allowClear
            showSearch
          />
        </div>
        <div className="w-64">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            District
          </label>
          <Select
            placeholder="All districts"
            style={{ width: "100%" }}
            options={districtOptions}
            value={selectedDistrict}
            onChange={(val) => setSelectedDistrict(val ?? null)}
            disabled={!selectedState}
            allowClear
            showSearch
          />
        </div>
      </div>

      {/* Transfer */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <Transfer
          dataSource={dataSource}
          titles={["Available Pincodes", "Selected Pincodes"]}
          targetKeys={targetKeys}
          onChange={(keys) => setTargetKeys(keys)}
          render={(item) => item.title}
          showSearch
          filterOption={(input, item) =>
            item.title.toLowerCase().includes(input.toLowerCase())
          }
          styles={{
            section: {
              width: 265,
              height: 320,
            },
          }}
          className="mb-6"
        />
      )}

      {/* Locality Type picker — shown only when something is selected */}
      {targetKeys.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Assign Locality Type to{" "}
            <span className="text-orange-500 font-semibold">
              {targetKeys.length}
            </span>{" "}
            selected pincode(s):
          </p>
          <Radio.Group
            value={localityType}
            onChange={(e) => setLocalityType(e.target.value)}
          >
            <Space orientation="horizontal">
              {LOCALITY_TYPES.map((lt) => (
                <Radio.Button
                  key={lt}
                  value={lt}
                  style={
                    localityType === lt
                      ? { borderColor: "#f97316", color: "#f97316" }
                      : {}
                  }
                >
                  <MdOutlineLocationOn
                    size="1em"
                    className="inline mr-1 align-text-bottom"
                  />
                  {lt}
                </Radio.Button>
              ))}
            </Space>
          </Radio.Group>
        </div>
      )}

      <div className="border-t-2 border-neutral-100 pt-4 flex gap-3">
        <AppControl
          control="button"
          type="button"
          name={isAssigning ? "Assigning…" : "Assign Locality Type"}
          size="md"
          color="orange"
          loading={isAssigning}
          disabled={!canAssign || isAssigning}
          onClick={handleAssign}
        />
        <AppControl
          control="button"
          type="button"
          name="Back to List"
          size="md"
          color="blue"
          onClick={() => navigate("/pincodes")}
        />
      </div>
    </Card>
  );
}

export default PincodeAssign;
